/* eslint-disable react/jsx-pascal-case */
import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  useRef,
} from "react";
import { collection, onSnapshot, getDocs } from "firebase/firestore";
import Header from "../../elements/Header";
import { useFirestore } from "../../firebase/FirestoreContext";
import "../../styles/global.css";
import { UserContext } from "../../context/UserContext";
import { collectionPath, EnvContext } from "../../context/EnvContext";
import config from "../../config";
import FeaturedCarousel_MobileV2 from "../../elements/FeaturedCarousel_MobileV2";
import FeaturedCarousel_DesktopV2 from "../../elements/FeaturedCarousel_DesktopV2";
import useWindowSize from "../../utils/useWindowSize";

import HorizontalListing from "../../elements/HorizontalListing";
import CategoryBrowse from "../../elements/CategoryBrowse";
import FindYourVibe from "../../elements/FindYourVibe";

import FAQ from "../../elements/FAQ";
import PageLayout from "../../elements/PageLayout";
import SearchAutocomplete from "../../elements/SearchAutocomplete";
import getListingsCollection from "../../utils/getListingsCollection";

const breakpoints = [390, 755];
const mq = breakpoints.map((bp) => `@media (min-width: ${bp}px)`);

const ShopPage = () => {
  const scrollContainerRef = useRef(null);
  const environment = useContext(EnvContext);
  const { width } = useWindowSize();
  const isMobile = width <= 768;

  const db = useFirestore();

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);

  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [lastViewedProducts, setLastViewedProducts] = useState([]);

  const { user } = useContext(UserContext);
  const userId = user ? user.uid : null;

  const fetchListingsForLastViewed = useCallback(
    async (listingIDs) => {
      if (!listingIDs || listingIDs.length === 0) return;

      try {
        // Reference to the Listings collection
        const listingsRef = getListingsCollection(db);

        // Fetch all listings from Firestore
        const listingsQuery = await getDocs(listingsRef);

        const listings = listingsQuery.docs
          .filter((doc) => listingIDs.includes(doc.id)) // Filter by matching IDs
          .map((doc) => ({ id: doc.id, ...doc.data() }));

        // Order listings based on the order of IDs
        const orderedListings = listingIDs
          .map((id) => {
            const listing = listings.find(
              (listing) => listing.seller_product_id === id,
            );
            if (!listing) {
              console.warn(`No matching listing found for ID: ${id}`);
            }
            return listing;
          })
          .filter(Boolean); // Remove undefined values

        // Update the state for "Last Viewed" products
        setLastViewedProducts(orderedListings);
      } catch (error) {
        console.error(
          "Error fetching listing data for last viewed products:",
          error,
        );
      }
    },
    [collectionPath, db],
  );

  // Latest Viewed Products
  const fetchLastViewedProducts = useCallback(async () => {
    if (!userId) return;

    try {
      // Reference to the RecentlyViewedProducts collection for the user
      const recentlyViewedRef = collection(
        db,
        `${collectionPath}Users/UsersList/DataBase/${userId}/RecentlyViewedProducts`,
      );
      const recentlyViewedSnapshot = await getDocs(recentlyViewedRef);

      // Sort the IDs by date (descending order) and extract the IDs
      const recentlyViewedIDs = recentlyViewedSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => b.date.toMillis() - a.date.toMillis()) // Sort by date
        .map((item) => item.id);

      // Fetch the listing data for the sorted IDs
      await fetchListingsForLastViewed(recentlyViewedIDs);
    } catch (error) {
      console.error("Error fetching last viewed products:", error);
    }
  }, [db, fetchListingsForLastViewed, userId]);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      getListingsCollection(db),
      (snapshot) => {
        const listings = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((listing) => listing.listing_price > 0);

        let filteredListings = listings;

        // Sort filtered listings
        filteredListings.sort((a, b) => {
          if (a.listing_stock_amount === 0 && b.listing_stock_amount !== 0) {
            return 1;
          }
          if (a.listing_stock_amount !== 0 && b.listing_stock_amount === 0) {
            return -1;
          }
          return 0;
        });

        // Sort sooperDeals
        filteredListings.sort((a, b) => {
          const isSooperDealA =
            a.sooperdeal_start_date && a.sooperdeal_price !== undefined;
          const isSooperDealB =
            b.sooperdeal_start_date && b.sooperdeal_price !== undefined;

          if (isSooperDealA && !isSooperDealB) return -1;
          if (!isSooperDealA && isSooperDealB) return 1;

          if (isSooperDealA && isSooperDealB) {
            return (
              new Date(a.sooperdeal_start_date) -
              new Date(b.sooperdeal_start_date)
            );
          }

          return 0;
        });

        // Update state with filtered and sorted listings
        setProducts(filteredListings);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching products:", error);
        setLoading(false);
      },
    );

    return () => unsubscribe();
  }, [db, environment, collectionPath]);

  // Feature products
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        // Reference to the FeaturedList/DataBase collection
        const featuredCollectionRef = collection(
          db,
          `${collectionPath}Listings/FeaturedList/DataBase`,
        );
        const featuredSnapshot = await getDocs(featuredCollectionRef);
        const featuredDocs = featuredSnapshot.docs;

        // Map over each document in the snapshot to extract necessary data
        const featuredData = featuredDocs.map((doc) => {
          const productData = doc.data();
          return {
            id: productData.seller_product_id || doc.id, // Fallback to doc.id if seller_product_id is missing
            ...productData,
            product_name: productData.product_name || "N/A",
            brand_name: productData.brand_name || "N/A",
            featured_url: productData.banner_target_url || "",
          };
        });
        setFeaturedProducts(featuredData);
      } catch (error) {
        console.error("Error fetching featured products:", error);
      }
    };

    // Invoke the fetch function
    fetchFeaturedProducts();
  }, [db, collectionPath]);

  useEffect(() => {
    fetchLastViewedProducts();
  }, [userId, db, collectionPath, fetchLastViewedProducts]);

  return (
    <PageLayout
      analyticsView="shop"
      analyticsTitle="Shop Page"
      showNewsletterModal
    >
      <Header products={products} />
      <div css={styles.container}>
        <SearchAutocomplete products={products} />
        {isMobile && (
          <FeaturedCarousel_MobileV2
            featuredCollection={"shoppage_1"}
            containerStyles={{ margin: "20px 0" }}
          />
        )}
        {!isMobile && (
          <FeaturedCarousel_DesktopV2
            featuredCollection={"shoppage_1"}
            containerStyles={{ margin: "20px auto", maxWidth: "900px" }}
          />
        )}

        {/* Categories */}
        <CategoryBrowse
          title={false}
          shopButton={false}
          containerStyle={styles.productGrid}
          numColumns={6}
          scrollable
        />
        {/* Vibes */}
        <FindYourVibe
          title={false}
          shopButton={false}
          numColumns={6}
          scrollable
        />

        <div ref={scrollContainerRef} css={styles.products}>
          {featuredProducts.length > 0 && (
            <h3
              style={{
                padding: "15px 10px 10px 10px",
                fontSize: 18,
                fontWeight: "300",
                display: "none",
              }}
            >
              Products
            </h3>
          )}
          {lastViewedProducts.length > 0 && (
            <>
              <HorizontalListing
                title="Previously Viewed"
                products={lastViewedProducts}
                displayShowAllButton={false}
                priceTheme="shop_page"
                ribbonSettings={{
                  blockedStatus: ["previouslyViewed"],
                }}
              />
              <div style={{ borderTop: "1px solid #D9D9D9", margin: "0" }} />
            </>
          )}

          {/* Horizontal sections for categories */}
          {loading ? (
            <div>Loading...</div>
          ) : (
            <>
              <HorizontalListing
                title="Popular Products"
                buttonText="Shop Popular"
                categoryLink="/product-category/popular-products"
                products={products.filter(
                  (product) => product.popular_product === true,
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Edibles"
                buttonText="Shop Edibles"
                categoryLink="/product-category/edibles"
                products={products.filter(
                  (product) => product.product_category === "Edibles",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Beverages"
                buttonText="Shop Beverages"
                categoryLink="/product-category/beverages"
                products={products.filter(
                  (product) => product.product_category === "Beverages",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Enhancers"
                buttonText="Shop Enhancers"
                categoryLink="/product-category/enhancers"
                products={products.filter(
                  (product) => product.product_category === "Enhancers",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Flower"
                buttonText="Shop Flower"
                categoryLink="/product-category/flower"
                products={products.filter(
                  (product) => product.product_category === "Flower",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Prerolls"
                buttonText="Shop Prerolls"
                categoryLink="/product-category/prerolls"
                products={products.filter(
                  (product) => product.product_category === "Prerolls",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Vape"
                buttonText="Shop Vape"
                categoryLink="/product-category/vape"
                products={products.filter(
                  (product) => product.product_category === "Vape",
                )}
                limit={10}
                priceTheme="shop_page"
              />

              <HorizontalListing
                title="Staff Picks For You"
                buttonText="Shop Staff Picks"
                categoryLink="/product-category/staff-picks"
                products={products.filter(
                  (product) => product.staff_pick === true,
                )}
                limit={10}
                priceTheme="shop_page"
              />
            </>
          )}
        </div>

        {isMobile && (
          <FeaturedCarousel_MobileV2 featuredCollection={"shoppage_bottom"} />
        )}
        {!isMobile && (
          <FeaturedCarousel_DesktopV2 featuredCollection={"shoppage_bottom"} />
        )}
        <FAQ />
      </div>
    </PageLayout>
  );
};

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    maxWidth: "1140px",
    margin: "0 auto",
    [mq[1]]: {
      gap: "15px",
    },
  },
  products: {
    marginTop: "30px",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    [mq[1]]: {
      gap: "45px",
    },
  },
  productGrid: {
    margin: "10px",
  },
  recentSearchesContainer: {
    marginBottom: "20px",
    padding: "10px",
    borderRadius: "5px",
  },
};

export default ShopPage;
